import Log from "@workspace/nextjs_utils/src/Log";
import { useEffect, useMemo, useRef } from "react";
import getSubscriptionClient from "./getSubscriptionClient";

type Options<T, V> = {
  variables?: V;
  onData: (data: T) => void;
};

const useSubscription = <T, V extends Record<string, unknown>>(
  query: string,
  { variables, onData }: Options<T, V>,
) => {
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const serializedVariables = useMemo(
    () => JSON.stringify(variables ?? {}),
    [variables],
  );

  useEffect(() => {
    const parsedVariables = JSON.parse(serializedVariables) as V;
    const subscription = getSubscriptionClient()
      .request({ query, variables: parsedVariables })
      .subscribe({
        error: (error: Error) => Log.error("subscription error", { error }),
        next: (value: { data?: unknown }) => {
          const subscriptionData = value.data;
          if (subscriptionData) {
            onDataRef.current(subscriptionData as T);
          }
        },
      });
    return () => subscription.unsubscribe();
  }, [query, serializedVariables]);
};

export default useSubscription;
